import React, { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { UIText } from "@pfs/react-ui-kit";
import { Button, Checkbox } from "@pc/ds-react";
import { useTranslation } from "src/hooks/useTranslation";
import { useRoundedRemainingUsage } from "src/hooks/useRoundedRemainingUsage";
import { TEXT_TAGS, EVENT_SCREEN } from "src/utils/enums";
import { useAppSelector } from "src/store/hooks";
import { trackEvent, GA4EventName, GA4Flow, GA4Action, GA4Step } from "src/analytics";
import Countdown from "../Countdown";
import MastercardDisclaimer from "../MastercardDisclaimer/MastercardDisclaimer";
import "./SavingsStartModal.scss";

export interface SavingsStartModalProps {
  remainingUsageAmount: number;
  showGroupMailingCheckbox: boolean;
  onConfirm: (groupMailingConsent: boolean) => void;
  onGroupMailingConsentChange?: (consent: boolean) => void;
}

const SavingsStartModal: React.FC<SavingsStartModalProps> = ({
  remainingUsageAmount,
  showGroupMailingCheckbox,
  onConfirm,
  onGroupMailingConsentChange,
}) => {
  const { t } = useTranslation();
  const [groupMailingConsent, setGroupMailingConsent] = useState(true);
  const [consentTooltipOpen, setConsentTooltipOpen] = useState(false);
  const consentLinkRef = useRef<HTMLButtonElement>(null);
  const consentTooltipRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!consentTooltipOpen) return undefined;
    const onPointerDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (
        !consentLinkRef.current?.contains(target) &&
        !consentTooltipRef.current?.contains(target)
      ) {
        setConsentTooltipOpen(false);
      }
    };
    document.addEventListener("mousedown", onPointerDown);
    return () => document.removeEventListener("mousedown", onPointerDown);
  }, [consentTooltipOpen]);

  useEffect(() => {
    if (!consentTooltipOpen) return;
    const positionTooltip = () => {
      const anchor = consentLinkRef.current;
      const tip = consentTooltipRef.current;
      if (!anchor || !tip) return;
      const anchorRect = anchor.getBoundingClientRect();
      const tipW = tip.offsetWidth;
      const tipH = tip.offsetHeight;
      const isRtl = getComputedStyle(document.documentElement).direction === "rtl";
      let top = anchorRect.bottom + 8;
      let left = isRtl ? anchorRect.left : anchorRect.right - tipW;
      if (left < 8) left = 8;
      if (left + tipW > window.innerWidth - 8) left = window.innerWidth - tipW - 8;
      if (top + tipH > window.innerHeight - 8) top = anchorRect.top - tipH - 8;
      tip.style.top = `${top}px`;
      tip.style.left = `${left}px`;
      tip.style.visibility = "visible";
    };
    const frame = requestAnimationFrame(positionTooltip);
    window.addEventListener("scroll", positionTooltip, true);
    window.addEventListener("resize", positionTooltip);
    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener("scroll", positionTooltip, true);
      window.removeEventListener("resize", positionTooltip);
    };
  }, [consentTooltipOpen]);

  const handleConsentChange = (value: boolean) => {
    setGroupMailingConsent(value);
    onGroupMailingConsentChange?.(value);
  };

  const handleConfirm = () => {
    trackEvent(GA4EventName.TRAVEL_SERVICES_FUNNEL, {
      flow: GA4Flow.FLIGHTS,
      action: GA4Action.CONTINUE_AFTER_ACCEPTANCE,
      step: GA4Step.OTP_2_ACCEPTANCE_POPUP,
      element_clicked: "continue",
      customer_id: customerIdEncrypted || "",
    });
    onConfirm(groupMailingConsent);
  };

  const displayAmount = useRoundedRemainingUsage(remainingUsageAmount);
  const formattedAmount = displayAmount.toLocaleString("he-IL");
  const expiryTime = useAppSelector((state) => state.user.payCardActivationExpiryTime);
  const personalId = useAppSelector((state) => state.user.personalId);
  const customerIdEncrypted = useAppSelector((state) => state.app.customerIdEncrypted);

  return (
    <div className="savings-start-modal">
      <div className="savings-start-modal__subtitle">
        <UIText
          text={t("savingsStartModal.subtitle")}
          tag={TEXT_TAGS.P}
          size={18}
          weight={400}
        />
      </div>

      <div className="savings-start-modal__body">
        <UIText
          text={t("savingsStartModal.remainingCredit", { amount: formattedAmount })}
          tag={TEXT_TAGS.P}
          size={18}
          weight={400}
        />
      </div>

      <div className="savings-start-modal__card-info">
        <UIText
          text={t("savingsStartModal.cardOpenFor")}
          tag={TEXT_TAGS.P}
          size={18}
          weight={400}
        />
        <span className="savings-start-modal__duration" dir="ltr">
          <Countdown
            expiryTime={expiryTime}
            format="hh:mm:ss"
          />
        </span>
      </div>

      {showGroupMailingCheckbox && (
        <div className="savings-start-modal__consent-row">
          <Checkbox
            checked={groupMailingConsent}
            onChange={handleConsentChange}
            labelFontSize={14}
            aria-label={`${t("savingsStartModal.groupMailingConsentLabel")}${t("savingsStartModal.groupMailingConsentLinkText")}`}
            label={
              <>
                {t("savingsStartModal.groupMailingConsentLabel")}
                <button
                  ref={consentLinkRef}
                  type="button"
                  className="savings-start-modal__consent-link"
                  aria-expanded={consentTooltipOpen}
                  onClick={(e) => {
                    e.stopPropagation();
                    setConsentTooltipOpen((open) => !open);
                    trackEvent(GA4EventName.TRAVEL_SERVICES_FUNNEL, {
                      flow: GA4Flow.FLIGHTS,
                      action: GA4Action.CLICK_FOR_MORE_DETAILS,
                      step: GA4Step.OTP_2_ACCEPTANCE_POPUP,
                      element_clicked: "more details",
                      customer_id: customerIdEncrypted || "",
                    });
                  }}
                >
                  {t("savingsStartModal.groupMailingConsentLinkText")}
                </button>
                {consentTooltipOpen &&
                  createPortal(
                    <div
                      ref={consentTooltipRef}
                      className="savings-start-modal__consent-tooltip savings-start-modal__consent-tooltip--portal"
                      role="tooltip"
                    >
                      <UIText
                        text={t("savingsStartModal.groupMailingConsentTooltip")}
                        tag={TEXT_TAGS.P}
                        size={13}
                        weight={400}
                        style="savings-start-modal__consent-tooltip-body"
                      />
                    </div>,
                    document.body,
                  )}
              </>
            }
          // className="savings-start-modal__consent-check"
          />
        </div>
      )}

      <div className="savings-start-modal__actions">
        <Button
          onClick={handleConfirm}
          variant="primary"
          showEndIcon
          disabled={showGroupMailingCheckbox && !groupMailingConsent}
          style={{ width: 420 }}
        >
          {t("savingsStartModal.confirmButton")}
        </Button>
      </div>

      <div className="savings-start-modal__disclaimer">
        <MastercardDisclaimer eventScreen={EVENT_SCREEN.FLIGHTS} />
      </div>
    </div>
  );
};

export default SavingsStartModal;
